import type { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { TemplateType } from 'modules/manager/domain/template-type';
import { ReviewClipKind } from 'modules/match-video/domain/interfaces';

/**
 * Template types an intro can be drawn from: the video intro first; a set made before the video types has only image
 * templates, so one match with teams, score, date and place stands in — «Результаты» first, a review is made after
 * the match, so the score is known.
 */
const INTRO_TYPES: readonly TemplateType[] = [TemplateType.VIDEO_INTRO, TemplateType.RESULTS, TemplateType.SCHEDULE];

/** The plate type per clip kind; a clip «other» never has a plate. */
const PLATE_TYPES: Readonly<Partial<Record<ReviewClipKind, TemplateType>>> = {
  goal: TemplateType.VIDEO_GOAL_PLATE,
  danger: TemplateType.VIDEO_DANGER_PLATE,
};

/** The set's template of this type: its default for the kind first, then by the set's own order. */
function pickOfType(templates: readonly MediaManagerTemplate[], type: TemplateType): MediaManagerTemplate | null {
  const candidates = templates
    .filter((template) => template.templateType === type)
    .sort((a, b) => Number(b.isDefaultForKind) - Number(a.isDefaultForKind) || a.order - b.order || a.id - b.id);
  return candidates[0] ?? null;
}

/** The intro of a review from a template set: the first type of `INTRO_TYPES` the set has. `null` — none of them. */
export function pickIntroTemplate(templates: readonly MediaManagerTemplate[]): MediaManagerTemplate | null {
  for (const type of INTRO_TYPES) {
    const picked = pickOfType(templates, type);
    if (picked) return picked;
  }
  return null;
}

/** The plates of a review from a template set; a kind the set has no plate for is left out. */
export function pickPlateTemplates(templates: readonly MediaManagerTemplate[]): Partial<Record<ReviewClipKind, MediaManagerTemplate>> {
  const plates: Partial<Record<ReviewClipKind, MediaManagerTemplate>> = {};
  for (const [kind, type] of Object.entries(PLATE_TYPES) as [ReviewClipKind, TemplateType][]) {
    const picked = pickOfType(templates, type);
    if (picked) plates[kind] = picked;
  }
  return plates;
}
